import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LoaderCircle, MessageCircle } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useMarca } from "@/components/providers/MarcaProvider";
import { ClienteService } from "@/services";
import { TelefoneContatoDTO } from "@/types/TelefoneContatoDTO";
import { TipoSeguro } from "@/types/TipoSeguro";
import { getTipoSeguroLabel } from "@/types/enum/TipoSeguro";
import { getTenant } from "@/utils/TenantUtils";
import { linkWhatsapp, mensagemRenovacao } from "@/utils/whatsapp";

export type SeguroAviso = {
  segurado: { id?: string; nome: string; telefones?: TelefoneContatoDTO[] };
  corretor: { nome?: string };
  tipoSeguro: TipoSeguro;
  placa?: string;
  vigenciaFinal?: string | Date;
};

// Mensagem de renovação pronta (editável) e um botão de WhatsApp por telefone do segurado.
// Sem os telefones (lista de seguros), busca o segurado ao abrir.
export const AvisarClienteDialog = ({ seguro, onClose }: { seguro?: SeguroAviso; onClose: () => void }) => {
  const { empresa } = useMarca();
  const [mensagem, setMensagem] = useState('');
  const [telefones, setTelefones] = useState<TelefoneContatoDTO[]>();

  useEffect(() => {
    if (!seguro) return;
    setMensagem(mensagemRenovacao({
      segurado: seguro.segurado.nome,
      corretor: seguro.corretor.nome || '',
      corretora: empresa,
      tipo: getTipoSeguroLabel(seguro.tipoSeguro),
      placa: seguro.placa,
      vigenciaFinal: String(seguro.vigenciaFinal).substring(0, 10),
    }));
    setTelefones(seguro.segurado.telefones);
    if (!seguro.segurado.telefones && seguro.segurado.id) {
      ClienteService.getByCodigo(seguro.segurado.id)
        .then(({ data }) => setTelefones(data.telefones || []))
        .catch(() => setTelefones([]));
    }
  }, [seguro, empresa]);

  return (
    <Dialog open={!!seguro} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Avisar {seguro?.segurado.nome}</DialogTitle>
          <DialogDescription>Confira a mensagem e escolha o telefone. O WhatsApp abre com o texto pronto para enviar.</DialogDescription>
        </DialogHeader>
        <Textarea value={mensagem} onChange={e => setMensagem(e.target.value)} rows={5} aria-label="Mensagem" />
        <div className="space-y-2">
          {telefones === undefined && <LoaderCircle className="h-5 w-5 animate-spin text-muted-foreground" />}
          {telefones?.map((t, i) => (
            <Button key={i} asChild className="w-full justify-start">
              <a href={linkWhatsapp(t.telefone, mensagem)} target="_blank" rel="noopener noreferrer" onClick={onClose}>
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp: {t.descricao} {t.telefone}
              </a>
            </Button>
          ))}
          {telefones?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhum telefone cadastrado.{' '}
              {seguro?.segurado.id && <Link className="font-medium text-primary hover:underline" to={`/${getTenant()}/segurado/${seguro.segurado.id}`}>Cadastre um telefone no segurado</Link>}
            </p>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Fechar</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
